import { Auth } from "aws-amplify";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../constants/routes";
import { useCompanyStore } from "../../../../stores/useCompanyStore";
import { usePoliciesContext } from "../../../../context/PoliciesContextProvider";
import { useUserContext } from "../../../../context/UserContextProvider";
import Api from "../../../../util/api";
import {
  clearSelectedCompany,
  setSelectedCompanyMembership,
} from "../../../../util/company-utils";
import { SubscriptionOptionEnum } from "@fena/toolkit-types";

const DEFAULT_DOMAINS = [
  "https://toolkit.develop.fena.co",
  "https://toolkit.staging.fena.co",
  "https://toolkit.fena.co",
  "http://localhost:3000",
];

const envDomainMap = new Map<string, string>([
  ["dev", "https://toolkit.develop.fena.co"],
  ["stage", "https://toolkit.staging.fena.co"],
  ["prod", "https://toolkit.fena.co"],
]);

export async function canUseDomain(customDomain = "", sessionToken?: string) {
  const url = new URL(window.location.href);

  // Check if we're receiving a session token in URL
  const urlParams = new URLSearchParams(window.location.search);
  const incomingSessionToken = urlParams.get("session_token");
  if (incomingSessionToken) {
    // Update API headers with the received token
    Api.updateHeadersWithToken(incomingSessionToken);
    // Remove the token from URL for security
    urlParams.delete("session_token");
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${
        urlParams.toString() ? "?" + urlParams.toString() : ""
      }`
    );
    return;
  }

  if (!DEFAULT_DOMAINS.includes(url.origin)) {
    const canUseDomain = await Api.getCanUseDomain(url.origin);

    if (!canUseDomain) {
      window.location.replace(
        envDomainMap.get(process.env.REACT_APP_ENV as string) ??
          "https://toolkit.fena.co"
      );
    }
  } else if (customDomain) {
    const canUseDomain = await Api.getCanUseDomain(customDomain);

    if (canUseDomain) {
      // Create URL with session token
      const redirectUrl = new URL(customDomain);
      if (sessionToken) {
        redirectUrl.searchParams.set("session_token", sessionToken);
      }
      window.location.replace(redirectUrl.toString());
    }
  }
}

export const useSignIn = () => {
  const navigate = useNavigate();

  const { setUser } = useUserContext();
  const { setCompany, refreshSettings } = useCompanyStore();
  const { setPolicies } = usePoliciesContext();
  const [loading, setLoading] = useState(false);

  const signIn = async (email: string, password: string) => {
    // Check if we have a session token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const sessionToken = urlParams.get("session_token");
    if (sessionToken) {
      Api.updateHeadersWithToken(sessionToken);
      // Remove the token from URL for security
      urlParams.delete("session_token");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${
          urlParams.toString() ? "?" + urlParams.toString() : ""
        }`
      );
    }
    setLoading(true);
    const signinResult = await Auth.signIn(email, password);
    if (signinResult?.challengeName) {
      return { challengedUser: signinResult };
    }
    const session = await Auth.currentSession();
    const accessToken = session.getIdToken();
    const jwt = accessToken.getJwtToken();
    Api.updateHeadersWithToken(jwt);
    clearSelectedCompany();
    let user;
    try {
      user = await Api.getUser();
    } catch (error: any) {
      if (error.status === 403 || error.status === 401) {
        return { user: null };
      }
      return { user: null };
    }
    try {
      await Api.updateLastLogin();
    } catch (e) {
      console.error(e);
    }
    setUser({
      ...user,
      cognitoReferenceId: accessToken.payload.sub,
      selectedCompanyMembership: user.companyMemberships[0],
    });
    const selectedMembership = setSelectedCompanyMembership(
      user.companyMemberships
    );
    if (!selectedMembership) {
      navigate(AppRoutes.Open.Auth.SUSPENDED_USER);
      // crutch
      return { user: null };
    }
    const companyData = await Api.getCompany();

    const policiesAsMap = new Map(
      Object.entries(selectedMembership.role.policies)
    );
    const currentSubscription = await Api.getCurrentSubscriptions();

    const customizationSettings = await Api.getParentCustomizationSettings();
    const customDomain = customizationSettings?.partner_dashboard?.domain;

    await canUseDomain(customDomain, jwt);

    // const policiesAsMap = new Map(Object.entries(user.role.policies));
    setPolicies(policiesAsMap);
    setCompany({
      ...companyData,
      currentSubscription: currentSubscription.data
        ?.filter(
          (el: any) =>
            el?.details?.subscriptionOption ===
              SubscriptionOptionEnum.PAYMENTS_ONLY ||
            el?.details?.subscriptionOption === SubscriptionOptionEnum.TOOLKIT
        )
        .sort((a: any, b: any) => {
          return (
            new Date(String(b?.createdAt)).getTime() -
            new Date(String(a?.createdAt)).getTime()
          );
        })[0],
    });

    setLoading(false);
    return { user, companyData, accessToken };
  };

  const confirmSignIn = async (
    cognitoUser: any,
    verificationCode: string,
    mfaType?: string
  ) => {
    setLoading(true);
    await Auth.confirmSignIn(cognitoUser, verificationCode, "SMS_MFA");
    const session = await Auth.currentSession();
    const accessToken = session.getIdToken();
    const jwt = accessToken.getJwtToken();

    Api.updateHeadersWithToken(jwt);
    const user = await Api.getUser();
    const selectedMembership = setSelectedCompanyMembership(
      user.companyMemberships
    );
    const companyData = await Api.getCompany();
    const currentSubscription = await Api.getCurrentSubscriptions();
    setUser({
      ...user,
      cognitoReferenceId: accessToken.payload.sub,
      selectedCompanyMembership: selectedMembership,
    } as any);
    if (!selectedMembership) {
      navigate(AppRoutes.Open.Auth.SUSPENDED_USER);
      // crutch
      return { user: null };
    }

    const policiesAsMap = new Map(
      Object.entries(selectedMembership.role.policies)
    );
    setPolicies(policiesAsMap);
    setCompany({
      ...companyData,
      currentSubscription: currentSubscription.data?.sort((a: any, b: any) => {
        return (
          new Date(String(b?.createdAt)).getTime() -
          new Date(String(a?.createdAt)).getTime()
        );
      })[0],
    });
    await refreshSettings();
    return { user, companyData, accessToken };
  };
  const updateLoading = (val: boolean) => {
    setLoading(val);
  };
  return { signIn, loading, confirmSignIn, updateLoading };
};
